import { getCareerAggregates, type CareerAggregates } from "@dawn/adapters";
import { getStoredLocale } from "@dawn/i18n";
import {
  createGameLifecycle,
  type GameLaunchOptions,
  type WorldSavedGameBundle,
} from "@site/launch/gameLauncher";
import { KofiButton } from "@site/components/KofiButton";
import { LandingChronicleModal } from "@site/modals/LandingChronicleModal";
import { LandingFooter } from "@site/components/LandingFooter";
import { LandingPage } from "@site/pages/LandingPage";
import {
  hasLandingResumeSave,
  LandingResumeModal,
} from "@site/modals/LandingResumeModal";
import { LandingSettingsModal } from "@site/modals/LandingSettingsModal";
import { shouldShowGameKofi } from "@site/config/loadKofiButton";
import { useLocale } from "@site/LocaleContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "@site/LandingApp.module.css";

type LandingModalId = "settings" | "resume" | "chronicle";

interface LandingAppProps {
  canvas: HTMLCanvasElement;
  hudRoot: HTMLElement;
}

function readCareerAggregates(): CareerAggregates {
  return getCareerAggregates();
}

export function LandingApp({ canvas, hudRoot }: LandingAppProps) {
  const { setLocale } = useLocale();
  const [landingVisible, setLandingVisible] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);
  const [openModal, setOpenModal] = useState<LandingModalId | null>(null);
  const [career, setCareer] = useState<CareerAggregates>(readCareerAggregates);
  const [lifecycle] = useState(() => createGameLifecycle({ canvas, hudRoot }));

  const playButtonRef = useRef<HTMLButtonElement>(null);
  const gearButtonRef = useRef<HTMLButtonElement>(null);
  const chronicleButtonRef = useRef<HTMLButtonElement>(null);
  const [gameKofiContainer] = useState(() =>
    document.querySelector<HTMLElement>(".game-kofi"),
  );
  const showGameKofi = shouldShowGameKofi();

  const refreshCareer = useCallback((): void => {
    setCareer(readCareerAggregates());
  }, []);

  const closeModal = useCallback((): void => {
    setOpenModal(null);
  }, []);

  useEffect(() => {
    return () => {
      lifecycle.dispose();
    };
  }, [lifecycle]);

  useEffect(() => {
    if (!showGameKofi) {
      gameKofiContainer?.remove();
    }
  }, [gameKofiContainer, showGameKofi]);

  const runLaunch = useCallback(
    async (options: GameLaunchOptions): Promise<void> => {
      setIsLaunching(true);

      try {
        await lifecycle.launch(options, {
          onBecameActive: () => {
            setLandingVisible(false);
            setOpenModal(null);
          },
          onExit: () => {
            setLandingVisible(true);
            setIsLaunching(false);
            setOpenModal(null);
            setLocale(getStoredLocale());
            refreshCareer();
          },
          onFailed: () => {
            setIsLaunching(false);
          },
        });
      } catch {
        // Logged inside createGameLifecycle.
      }
    },
    [lifecycle, refreshCareer, setLocale],
  );

  const handlePlay = (): void => {
    if (hasLandingResumeSave()) {
      setOpenModal("resume");
      return;
    }

    void runLaunch({});
  };

  const handleSettings = (): void => {
    setOpenModal((current) => (current === "settings" ? null : "settings"));
  };

  const handleChronicle = (): void => {
    setOpenModal("chronicle");
  };

  const handleContinue = (bundle: WorldSavedGameBundle): void => {
    void runLaunch({
      worldSavedSnapshot: bundle.world,
      worldSavedPresentation: bundle.presentation,
      worldSavedMapScenario: bundle.mapScenario ?? null,
    });
  };

  const handleNewGame = (): void => {
    void runLaunch({});
  };

  return (
    <>
      <div className={styles.chrome}>
        <LandingPage
          landingVisible={landingVisible}
          isLaunching={isLaunching}
          career={career}
          playButtonRef={playButtonRef}
          gearButtonRef={gearButtonRef}
          chronicleButtonRef={chronicleButtonRef}
          onPlay={handlePlay}
          onSettings={handleSettings}
          onChronicle={handleChronicle}
        />
        <LandingFooter />
      </div>
      <LandingSettingsModal
        isOpen={openModal === "settings"}
        onClose={closeModal}
        returnFocusRef={gearButtonRef}
        restoreFocus={openModal === null}
      />
      <LandingResumeModal
        isOpen={openModal === "resume"}
        onClose={closeModal}
        returnFocusRef={playButtonRef}
        restoreFocus={openModal === null}
        buttonsDisabled={isLaunching}
        onContinue={handleContinue}
        onNewGame={handleNewGame}
      />
      <LandingChronicleModal
        isOpen={openModal === "chronicle"}
        onClose={closeModal}
        returnFocusRef={chronicleButtonRef}
        restoreFocus={openModal === null}
        onCleared={refreshCareer}
      />
      {showGameKofi && gameKofiContainer
        ? createPortal(<KofiButton />, gameKofiContainer)
        : null}
    </>
  );
}
